import React, { useState, useEffect } from "react";
import { useChat } from "../../../../Layout/ChatProvider/ChatProvider"; // Import the custom hook
import { updateCompany } from "../../../../../../services/api";
import "./EditCompanyModal.css";

interface EditCompanyModalProps {
    onClose: () => void;
    onConfirm: (companyData: {
        name: string;
        id: string;
        occupation: string;
        billing_amount: string;
        operator_user_id: string | null;
        status_description: string;
    }) => void;
    initialData?: {
        name?: string;
        id?: string;
        occupation?: string;
        billing_amount?: string;
        operator_user_id?: string;
        status_description?: string;
    };
    companyId: string;
}

const EditCompanyModal: React.FC<EditCompanyModalProps> = ({
                                                               onClose,
                                                               onConfirm,
                                                               initialData = {},
                                                               companyId,
                                                           }) => {
    const [name, setName] = useState(initialData.name || "");
    const [id, setId] = useState(initialData.id || "");
    const [occupation, setOccupation] = useState(initialData.occupation || "");
    const [billingAmount, setAmount] = useState(initialData.billing_amount || "");
    const [operatorUserId, setOperatorUserId] = useState(initialData.operator_user_id || "");
    const [statusDescription, setStatusDescription] = useState(initialData.status_description || ""); // ✅ NEW
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { operatorUsers } = useChat();

    useEffect(() => {
        setOperatorUserId(initialData.operator_user_id || "");
    }, [initialData.operator_user_id]);

    useEffect(() => {
        setStatusDescription(initialData.status_description || ""); // ✅ make sure it's always set
    }, [initialData.status_description]);

    const handleSubmit = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const result = await updateCompany({
                id: Number(companyId),
                name,
                occupation,
                billing_amount: billingAmount,
                operator_user_id: operatorUserId || undefined,
                status_description: statusDescription || "", // ✅ NEW
            });

            if (result.success) {
                onConfirm({
                    name,
                    id,
                    occupation,
                    billing_amount: billingAmount,
                    operator_user_id: operatorUserId || null,
                    status_description: statusDescription || "", // ✅ NEW
                });
                onClose();
            } else {
                setError(result.message || "Failed to update company");
            }
        } catch (error) {
            setError(error instanceof Error ? error.message : "An unknown error occurred");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="edit-company-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className="edit-company-modal">
                <button className="close-btn" onClick={onClose}>×</button>
                <h2 className="modal-title">עריכת חברה</h2>

                <div className="input-group">
                    <label>שם חברה</label>
                    <input type="text" value={name} onChange={(e) => setName(e.target.value)} />
                </div>

                <div className="input-group">
                    <label>ח.פ.</label>
                    <input type="text" value={id} readOnly className="readonly-input" />
                </div>

                <div className="input-group">
                    <label>תחום עיסוק</label>
                    <input type="text" value={occupation} onChange={(e) => setOccupation(e.target.value)} />
                </div>

                <div className="input-group">
                    <label>סכום חודשי</label>
                    <input type="text" value={billingAmount} onChange={(e) => setAmount(e.target.value)} />
                </div>

                <div className="input-group">
                    <label>מנהל תזרים</label>
                    <select
                        value={operatorUserId}
                        onChange={(e) => setOperatorUserId(e.target.value)}
                    >
                        <option value="">ללא מנהל תזרים</option>
                        {operatorUsers.map((user) => (
                            <option key={user.id} value={user.id.toString()}>
                                {user.first_name} {user.last_name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="input-group">
                    <label>הערות חברה</label> {/* ✅ NEW */}
                    <textarea
                        value={statusDescription}
                        onChange={(e) => setStatusDescription(e.target.value)}
                        rows={3}
                        placeholder="הזן הערות עבור החברה..."
                    />
                </div>

                {error && <div className="error-message">{error}</div>}

                <div className="modal-actions">
                    <button className="cancel-btn" onClick={onClose}>ביטול</button>
                    <button className="confirm-btn" onClick={handleSubmit} disabled={isLoading}>
                        {isLoading ? "מעדכן..." : "אישור"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditCompanyModal;
